/**
 * MAA: force the header's <overflow-list> to recompute once a window resize has settled.
 *
 * WHY THIS EXISTS
 *
 * Horizon's <overflow-list> (assets/overflow-list.js) recomputes from a
 * ResizeObserver, and #reflowItems brackets itself:
 *
 *     #reflowItems = (listHeight = 0, lastVisibleElement = null) => {
 *       this.#unobserveChanges();
 *       ... measure and move items ...
 *       this.#observeChanges();
 *     };
 *
 * The observer is a ResizeNotifier (assets/utilities.js), which deliberately
 * swallows the FIRST callback after every observe():
 *
 *     constructor(callback) {
 *       super((entries) => {
 *         if (this.#initialized) return callback(entries, this);
 *         this.#initialized = true;
 *       });
 *     }
 *     disconnect() { this.#initialized = false; super.disconnect(); }
 *
 * A ResizeObserver always delivers one callback on observe(), so swallowing it
 * is correct in isolation. But during a drag the sequence is:
 *
 *   resize -> #handleChange -> rAF + setTimeout -> #reflowItems
 *     -> unobserve (resets the swallow flag)
 *     -> measure at THAT instant
 *     -> observe (the next callback, i.e. the CURRENT size, is swallowed)
 *
 * If the pointer stops during that window, the settle size is the one that gets
 * swallowed and no further resize events follow. The list keeps whatever layout
 * it computed mid-drag. Dragging outward, that is a narrower width - so items
 * stay parked in "More" at a width where they would fit inline. This is what
 * Nate reported: the menu does not come back when the window is widened.
 *
 * The component already exposes the recompute we need. assets/overflow-list.js:
 *
 *     this.addEventListener('reflow', (event) => {
 *       this.#reflowItems(0, event.detail.lastVisibleElement);
 *     });
 *
 * so this file only re-fires the component's own path after the resize settles.
 * Nothing here reaches into private state, and `detail: {}` is required - the
 * handler reads event.detail.lastVisibleElement and would throw on a bare Event.
 * listHeight defaults to 0 and is guarded by `if (listHeight > 0)`, so passing
 * nothing pins no height.
 *
 * assets/overflow-list.js, assets/header.js and assets/utilities.js are all
 * byte-identical to the pristine Horizon 4.1.4 reference theme (180062552311),
 * so this is stock behaviour, not something MAA introduced. Fixing it additively
 * rather than patching the stock component keeps the next Horizon upgrade clean.
 */
(() => {
  if (window.__maaHeaderReflowBound) return;
  window.__maaHeaderReflowBound = true;

  const SETTLE_MS = 160;
  let timer = null;

  const reflow = () => {
    const lists = document.querySelectorAll('#header-component overflow-list');
    for (const list of lists) {
      list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
    }
  };

  window.addEventListener(
    'resize',
    () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(reflow, SETTLE_MS);
    },
    { passive: true }
  );
})();
